import { useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useQuery } from '@tanstack/react-query';
import { useQueues, useSaveQueue, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { DragPicker } from '../../components/ui/DragPicker.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, SubHead, SectionHead, EditorHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api } from '../../lib/api/client.js';
import { formatDuration } from '../../lib/format.js';
import type { Queue, Destination } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';
import { WorkspaceControlsEditor } from './WorkspaceControlsEditor.js';
import type { PanelMode } from './flows/panel.js';
import { resolveWorkspaceControls, type WorkspaceControlsSet } from '@arcnet/contracts';

export function QueuesTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need queue:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('queue:write');
  const queues = useQueues(tenantId);
  const del = useDeleteTms(tenantId, 'queues');
  const [editing, setEditing] = useState<Queue | null>(null);
  const [creating, setCreating] = useState(false);

  // An editor takes the list's place, so there is one thing on the page (owner, 29 September 2026).
  if (creating || editing) {
    return (
      <>
      <EditorHead back="Queues" onBack={() => { setCreating(false); setEditing(null); }}
        title={editing ? editing.name : 'New queue'} />
      <QueueEditor tenantId={tenantId} existing={editing}
        onDone={() => { setCreating(false); setEditing(null); }} />
      </>
    );
  }

  return (
    <>
      <SectionHead title="Queues"
        sub="Hold callers until an agent is free, with position announcements."
        action={mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating(true); setEditing(null); }}>
          <Icon name="plus" size={13} />
          New queue
        </button>
        )} />


      {queues.error && <ErrorBanner error={queues.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Strategy</th>
                <th className="arc-td-num">Agents</th>
                <th className="arc-td-num">Max wait</th>
                <th>On timeout</th><th />
              </tr>
            </thead>
            {queues.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {queues.data?.data.map((q) => (
                  <tr key={q.id} className="arc-row-editable" onDoubleClick={() => { setEditing(q); setCreating(false); }}>
                    <td style={{ fontWeight: 550 }}>{q.name}</td>
                    <td className="arc-mono">{q.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td>{q.strategy.replace(/_/g, ' ')}</td>
                    <td className="arc-td-num">{q.agents.length}</td>
                    <td className="arc-td-num">{formatDuration(q.max_wait_sec)}</td>
                    <td><DestinationLabel tenantId={tenantId} destination={q.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <RowAction kind="edit" label={`Edit ${q.name}`}
                          onClick={() => { setEditing(q); setCreating(false); }} />
                        )}
                        {mayWrite && (
                        <RowAction kind="delete" label={`Delete ${q.name}`}
                          onClick={() => {
                            void confirmAction({ title: `Delete queue '${q.name}'?`, action: 'Delete', danger: true })
                              .then((ok) => { if (ok) del.mutate(q.id); });
                          }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {queues.data !== undefined && queues.data?.data.length === 0 && (
          <Empty icon="queue" title="No queues"
            hint="A queue holds callers with music and announcements until an agent is free." />
        )}
      </div>
    </>
  );
}

export function QueueEditor({ tenantId, existing, onDone, panel }: {
  tenantId: string; existing: Queue | null; onDone: () => void;
  /** In a call flow's side panel: apply into the draft instead of saving. */
  panel?: PanelMode;
}) {
  const save = useSaveQueue(tenantId);
  const extensions = useExtensions(tenantId, { limit: 200 });

  const [name, setName] = useState(existing?.name ?? '');
  const [ext, setExt] = useState(existing?.extension ?? '');
  const [strategy, setStrategy] = useState(existing?.strategy ?? 'longest_idle');
  const [maxWait, setMaxWait] = useState(String(existing?.max_wait_sec ?? 600));
  const [wrapup, setWrapup] = useState(String(existing?.wrapup_sec ?? 10));
  const [mustMark, setMustMark] = useState(existing?.require_disposition ?? false);
  const [controls, setControls] = useState<WorkspaceControlsSet>(existing?.workspace_controls ?? {});
  const accountControls = useQuery({
    queryKey: ['agent-settings', tenantId],
    queryFn: () => api.get<{ workspace_controls: WorkspaceControlsSet }>(`/v1/tenants/${tenantId}/agent-settings`),
  });
  // Off unless the queue asks for it (0117): 30 days is what it starts at when switched on.
  const [sticky, setSticky] = useState(existing?.sticky_agent_days != null);
  const [stickyDays, setStickyDays] = useState(String(existing?.sticky_agent_days ?? 30));
  const [stickyRing, setStickyRing] = useState(String(existing?.sticky_ring_sec ?? 15));
  const [announce, setAnnounce] = useState(existing?.announce_position ?? false);
  const [announceEvery, setAnnounceEvery] = useState(String(existing?.announce_interval_sec ?? 60));
  // A recording played to waiting callers every so often (0143).
  const [periodic, setPeriodic] = useState<string>(existing?.periodic_media_id ?? '');
  const [periodicEvery, setPeriodicEvery] = useState(String(existing?.periodic_interval_sec ?? 60));
  const [timeout_, setTimeout_] = useState<Destination | null>(existing?.timeout_destination ?? null);
  // A key that lets a caller leave the queue, and where it sends them (0145).
  const [exitKey, setExitKey] = useState<string>(existing?.exit_key ?? '');
  const [exitTo, setExitTo] = useState<Destination | null>(existing?.exit_destination ?? null);
  const [agents, setAgents] = useState<string[]>(existing?.agents.map((a) => a.extension_id) ?? []);
  const [moh, setMoh] = useState<string>(existing?.moh_media_id ?? '');

  // The same query the Media screens use, so the list is shared.
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{
      data: Array<{ id: string; kind: string; name: string }>; moh_default_id?: string | null;
    }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const mohFiles = (media.data?.data ?? []).filter((m) => m.kind === 'moh');
  const spoken = (media.data?.data ?? []).filter((m) => m.kind === 'prompt' || m.kind === 'announcement' || m.kind === 'greeting');
  const accountMoh = mohFiles.find((m) => m.id === media.data?.moh_default_id)?.name;

  const gated = (extensions.data?.data ?? []).filter((e) => e.may_queue === false).length;

  return (
    <form
      id={panel?.formId}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        const body = {
          name, extension: ext || null, strategy,
          max_wait_sec: Number(maxWait), wrapup_sec: Number(wrapup),
          require_disposition: mustMark,
          workspace_controls: controls,
          sticky_agent_days: sticky ? Number(stickyDays) : null,
          sticky_ring_sec: Number(stickyRing),
          announce_position: announce,
          announce_interval_sec: Number(announceEvery),
          periodic_media_id: periodic || null,
          periodic_interval_sec: Number(periodicEvery),
          timeout_destination: timeout_,
          exit_key: exitKey && exitTo ? exitKey : null,
          exit_destination: exitKey && exitTo ? exitTo : null,
          moh_media_id: moh || null,
          agents: agents.map((id, i) => ({ extension_id: id, tier_level: 1, tier_position: i + 1 })),
        };
        if (panel) { panel.onApply(body); return; }
        save.mutate({ ...(existing ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
      }}
    >
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(100px, 0.5fr) minmax(180px, 1fr)', gap: 12 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="q-name">Name</label>
            <input id="q-name" className="arc-input" required value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Support" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="q-ext">Dial code</label>
            <input id="q-ext" className="arc-input arc-mono" value={ext}
              onChange={(e) => setExt(e.target.value)} placeholder="Optional" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="q-strat">Offer calls to</label>
            <select id="q-strat" className="arc-select" value={strategy}
              onChange={(e) => setStrategy(e.target.value as typeof strategy)}>
              <option value="longest_idle">The agent free longest</option>
              <option value="round_robin">Each agent in turn</option>
              <option value="ring_all">Every free agent at once</option>
              <option value="top_down">Agents in list order</option>
              <option value="agent_with_least_talk_time">The agent with least talk time</option>
              <option value="random">A random free agent</option>
            </select>
          </div>
        </div>
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub={strategy === 'top_down' ? 'Calls are offered to agents in this order: drag to reorder.' : undefined}>Agents</SubHead>
        <DragPicker
          chosenTitle="In this queue" availableTitle="Available" empty="No agents yet."
          ordered={strategy === 'top_down'}
          chosen={agents} onChange={setAgents}
          items={(extensions.data?.data ?? []).filter((e) => e.may_queue !== false).map((e) => ({
            id: e.id, code: e.number, label: e.display_name, detail: e.admin_group_name ?? undefined,
            search: `${e.number} ${e.display_name} ${e.admin_group_name ?? ''}`,
          }))}
        />
        {/* Said once, rather than listing seats that cannot be chosen (owner, 16 September 2026). */}
        {gated > 0 && (
          <p className="arc-hint">
            {gated} extension{gated === 1 ? '' : 's'} not shown: queue membership isn’t in their type.
          </p>
        )}
      </div>

      {/* What a waiting caller hears (0143), and how long they wait: one card, in the order the caller meets it. */}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead>While callers wait</SubHead>
        <div className="arc-grid arc-grid-2" style={{ marginBottom: 12 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="q-moh">Music</label>
            <select id="q-moh" className="arc-select" value={moh} onChange={(e) => setMoh(e.target.value)}>
              <option value="">{accountMoh ? `Account hold music (${accountMoh})` : 'Account hold music'}</option>
              {mohFiles.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="arc-row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <div className="arc-field" style={{ margin: 0, flex: 1 }}>
              <label className="arc-label" htmlFor="q-periodic">Message</label>
              <select id="q-periodic" className="arc-select" value={periodic} onChange={(e) => setPeriodic(e.target.value)}>
                <option value="">None</option>
                {spoken.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <p className="arc-hint">Played over the music, for example “Your call is important to us.” Upload recordings under Prompts.</p>
            </div>
            {periodic && (
              <div className="arc-field" style={{ margin: 0, width: 110 }}>
                <label className="arc-label" htmlFor="q-periodic-every">Every (s)</label>
                <input id="q-periodic-every" className="arc-input arc-num" inputMode="numeric" value={periodicEvery}
                  onChange={(e) => setPeriodicEvery(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
            )}
          </div>
        </div>

        <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Switch id="q-ann" checked={announce} onChange={setAnnounce} />
          <label htmlFor="q-ann" style={{ fontSize: 13, cursor: 'pointer' }}>
            Tell callers their place in the queue
            <span className="arc-muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              “You are number three in the queue.” First a few seconds after they join.
            </span>
          </label>
          {announce && (
            <div className="arc-field" style={{ margin: '0 0 0 auto', width: 130 }}>
              <label className="arc-label" htmlFor="q-ann-every">Repeat every (s)</label>
              <input id="q-ann-every" className="arc-input arc-num" inputMode="numeric"
                value={announceEvery} onChange={(e) => setAnnounceEvery(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
          )}
        </div>

        {/* Leaving by a key (0145, owner 29 September 2026: "bailout"). */}
        <div className="arc-row" style={{ gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
          <div className="arc-field" style={{ margin: 0, width: 150 }}>
            <label className="arc-label" htmlFor="q-exit-key">Key to leave the queue</label>
            <select id="q-exit-key" className="arc-select" value={exitKey} onChange={(e) => setExitKey(e.target.value)}>
              <option value="">None</option>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          {exitKey && (
            <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 240 }}>
              <label className="arc-label">Pressing {exitKey} sends the caller to</label>
              <DestinationPicker tenantId={tenantId} value={exitTo} onChange={setExitTo} allowNone />
            </div>
          )}
          <p className="arc-hint" style={{ flexBasis: '100%', margin: 0 }}>
            {exitKey && !exitTo
              ? 'Choose where the key goes, or it stays off.'
              : 'Tell callers in the message, for example “Press 1 to leave a voicemail.” Callers can press it any time while they wait.'}
          </p>
        </div>

        <div className="arc-row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="arc-field" style={{ margin: 0, width: 150 }}>
            <label className="arc-label" htmlFor="q-wait">Wait at most (s)</label>
            <input id="q-wait" className="arc-input arc-num" inputMode="numeric" value={maxWait}
              onChange={(e) => setMaxWait(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 240 }}>
            <label className="arc-label">If the wait is exceeded, send the caller to</label>
            <DestinationPicker tenantId={tenantId} value={timeout_} onChange={setTimeout_} allowNone />
          </div>
        </div>
      </div>

      {/* What agents' workspaces offer on this queue's calls (0153). */}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="What agents' workspaces offer them. An agent's extension can override this.">Workspace</SubHead>
        <WorkspaceControlsEditor value={controls} onChange={setControls} idPrefix="q-wsc" inheritLabel="Account default"
          inherit={resolveWorkspaceControls(accountControls.data?.workspace_controls, [], {}).controls} />
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead>After each call</SubHead>
        <div className="arc-field" style={{ maxWidth: 200 }}>
          <label className="arc-label" htmlFor="q-wrap">Wrap-up (s)</label>
          <input id="q-wrap" className="arc-input arc-num" inputMode="numeric" value={wrapup}
            onChange={(e) => setWrapup(e.target.value)} />
          <p className="arc-hint">After a call from this queue, before another is offered.</p>
        </div>

        {/* What this queue's calls need saying about them (0111). Beside the wrap-up time,
            because they are the same decision about the same moment. */}
        <div className="arc-row" style={{ gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
          <Switch id="q-must-mark" checked={mustMark} onChange={setMustMark} />
          <label htmlFor="q-must-mark" style={{ fontSize: 13, cursor: 'pointer' }}>
            Agents must mark a call from this queue before going ready
            <span className="arc-muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              They can still take a break while it is unmarked. An agent given their own answer on
              their extension overrides this.
            </span>
          </label>
        </div>

        {/* Sticky agents (0117, owner 25 September 2026). */}
        <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Switch id="q-sticky" checked={sticky} onChange={setSticky} />
          <label htmlFor="q-sticky" style={{ fontSize: 13, cursor: 'pointer' }}>
            Send a caller back to the agent who last dealt with them
            <span className="arc-muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              Calls the agent placed count as well as calls they answered. If they are busy, away or
              do not answer, the call goes into the queue as usual.
            </span>
          </label>
        </div>
        {sticky && (
          <div className="arc-row" style={{ gap: 12, marginTop: 10, flexWrap: 'wrap', marginLeft: 46 }}>
            <div className="arc-field" style={{ margin: 0, width: 200 }}>
              <label className="arc-label" htmlFor="q-sticky-days">Remember for (days)</label>
              <input id="q-sticky-days" className="arc-input arc-num" inputMode="numeric" value={stickyDays}
                onChange={(e) => setStickyDays(e.target.value.replace(/[^0-9]/g, ''))} />
              <p className="arc-hint">Refreshed by every answered call, so an active customer stays.</p>
            </div>
            <div className="arc-field" style={{ margin: 0, width: 200 }}>
              <label className="arc-label" htmlFor="q-sticky-ring">Ring them for (s)</label>
              <input id="q-sticky-ring" className="arc-input arc-num" inputMode="numeric" value={stickyRing}
                onChange={(e) => setStickyRing(e.target.value.replace(/[^0-9]/g, ''))} />
              <p className="arc-hint">Then the queue. 5 to 60.</p>
            </div>
          </div>
        )}
      </div>

      {!panel && (
        <div className="arc-row" style={{ gap: 8 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create queue'}
          </button>
          <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        </div>
      )}
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}
